import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    TableSortLabel,
    Typography,
} from "./TailwindMuiStubs";
import socket, { connectSocket, disconnectSocket } from "../../socket";

const RaceManagementCheckpoint = () => {
    const [crews, setCrews] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]); // Multi-select for categories
    const [checkpointFilter, setCheckpointFilter] = useState("Všetko"); // Filter for checkpoint status
    const [sortConfig, setSortConfig] = useState({ key: "start_number", direction: "asc" }); // Default sorting by start number
    const { selectedYear } = useOutletContext();

    useEffect(() => {
        // Ensure the socket connects
        connectSocket();

        // Fetch crews and categories from the backend
        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        const fetchCategories = () => {
            socket.emit("fetchCategories");
        };

        fetchCrews();
        fetchCategories();

        socket.on("crewsData", (data) => {
            setCrews(data);
        });

        socket.on("categoriesData", (data) => {
            setCategories(data);
        });

        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );
        });

        return () => {
            // Disconnect the socket when the component unmounts
            disconnectSocket();
            socket.off("crewsData");
            socket.off("categoriesData");
            socket.off("crewUpdated");
        };
    }, [selectedYear]); // Ensure this runs every time the component is mounted

    const handleCheckpointToggle = (crewId, currentCheckpoint) => {
        const newCheckpoint = !currentCheckpoint;
        socket.emit("updateCrewField", { crewId, field: "checkpoint", value: newCheckpoint });
    };

    const handleCategoryChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedCategories(typeof value === "string" ? value.split(",") : value);
    };

    const handleSort = (key) => {
        setSortConfig((prevConfig) => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
        }));
    };

    const sortedCrews = [...crews].sort((a, b) => {
        const aValue = a[sortConfig.key] || 0;
        const bValue = b[sortConfig.key] || 0;

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const filteredCrews = sortedCrews.filter((crew) => {
        // Only include crews with a start_time
        if (!crew.race_time?.start_time) {
            return false;
        }

        // Filter by selected categories
        if (selectedCategories.length > 0 && !selectedCategories.includes(crew.category?.name)) {
            return false;
        }

        // Filter by checkpoint status
        if (checkpointFilter === "Checkpointed" && !crew.race_time?.checkpoint) {
            return false;
        }
        if (checkpointFilter === "Not Checkpointed" && crew.race_time?.checkpoint) {
            return false;
        }

        return true;
    });

    return (
        <div>
            <div className="mb-6">
                <Typography variant="h4">Checkpoint Maratónu</Typography>
                <Typography variant="body2">
                    Sledovanie prechodu posádok cez medzičas (Checkpoint). Zobrazujú sa len odštartované lode.
                </Typography>
            </div>

            <div style={{ 
                marginBottom: "20px", 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "20px", 
                backgroundColor: "white",
                paddingBottom: "1rem",
                borderBottom: "1px solid #f3f4f6"
            }}>
                <FormControl sx={{ minWidth: "220px" }}>
                    <InputLabel>Filtrovať kategóriu</InputLabel>
                    <Select
                        value={selectedCategories[0] || ""}
                        onChange={(e) => setSelectedCategories([e.target.value])}
                    >
                        <MenuItem value="">Všetky kategórie</MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.name}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: "200px" }}>
                    <InputLabel>Status Checkpointu</InputLabel>
                    <Select
                        value={checkpointFilter}
                        onChange={(e) => setCheckpointFilter(e.target.value)}
                    >
                        <MenuItem value="Všetko">Všetko</MenuItem>
                        <MenuItem value="Checkpointed">Prešli</MenuItem>
                        <MenuItem value="Not Checkpointed">Neprešli</MenuItem>
                    </Select>
                </FormControl>
            </div>
            <div className="hidden md:block">
                <TableContainer
                    component={Paper}
                    sx={{
                        overflow: "visible",
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === "start_number"}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort("start_number")}
                                    >
                                        Štartovacie číslo
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Posádka</TableCell>
                                <TableCell>Kategória</TableCell>
                                <TableCell>Checkpoint</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCrews.map((crew) => (
                                <TableRow key={crew.id}>
                                    <TableCell>{crew.start_number || "N/A"}</TableCell>
                                    <TableCell>
                                        {crew.participants
                                            ? crew.participants
                                                .map((participant) => participant.name)
                                                .join(", ")
                                            : "N/A"}
                                    </TableCell>
                                    <TableCell>{crew.category?.name || "N/A"}</TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={crew.race_time?.checkpoint || false}
                                            onChange={() =>
                                                handleCheckpointToggle(
                                                    crew.id,
                                                    crew.race_time?.checkpoint || false
                                                )
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-4">
                {filteredCrews.map((crew) => (
                    <div key={`mob-${crew.id}`} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200 flex items-center justify-between">
                        <div className="flex-1 pr-4">
                            <div className="font-black text-2xl text-[#1E3A8A] mb-1">
                                #{crew.start_number || "---"}
                            </div>
                            <div className="text-[15px] font-bold text-gray-800 leading-tight mb-2">
                                {crew.participants
                                    ? crew.participants.map((p) => p.name).join(", ")
                                    : "Neznámi"}
                            </div>
                            <div className="inline-block px-3 py-1 bg-gray-100 rounded-lg text-[11px] font-black uppercase tracking-wider text-gray-600 border border-gray-200">
                                {crew.category?.name || "N/A"}
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex flex-col items-center gap-2">
                            <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest">
                                Checkpoint
                            </div>
                            <div className="scale-125">
                                <Checkbox
                                    checked={crew.race_time?.checkpoint || false}
                                    onChange={() =>
                                        handleCheckpointToggle(
                                            crew.id,
                                            crew.race_time?.checkpoint || false
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RaceManagementCheckpoint;
